import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios'
import { IUserModel } from 'src/interfaces'
import { AuthModule } from 'src/store'
import { AuthUtils, ClientUtils, LocalStorage } from 'src/utils'
import { axiosHiringApi } from '.'
import { ProfileApi } from '../profile'

/**
 * TODO: not implemented
 */
const refreshTokenUrl = '/auth/refresh'

const getRefreshToken = () => AuthUtils.getToken(true)

const clearCredentials = () => {
  AuthUtils.clear()
  AuthModule.unauthorized()
}

/**
 * @param {AxiosRequestConfig} config
 * @returns {AxiosRequestConfig}
 */
const injectAuthorization = (config: AxiosRequestConfig) => {
  const token = AuthUtils.getToken()
  config.headers = Object.assign(config.headers || {}, {})

  if (token) {
    config.headers.authorization = `Bearer ${token}`
  }

  return config
}

/**
 * @param {AxiosResponse} response
 */
const cacheResponseToken = (response: AxiosResponse) => {
  const newAuthorization = response.headers && response.headers.authorization
  const { token, refreshToken } = response.data
  if (newAuthorization && token) {
    AuthUtils.setToken(token)
  }
  if (newAuthorization && refreshToken) {
    AuthUtils.setToken(undefined, refreshToken)
  }

  return response
}

/**
 * @param {string} url
 * @returns {boolean}
 */
const isRefreshRequest = (url?: string) => String(url).endsWith(refreshTokenUrl)

const _authProgress: {
  CACHE_MS: number
  INTERVAL_MS: number
  REFRESHING: boolean
  RESPONSE: AxiosResponse | null
  ERROR: boolean | null
} = {
  CACHE_MS: 10000,
  INTERVAL_MS: 100,
  REFRESHING: false,
  RESPONSE: null,
  ERROR: false
}

const awaitNotRefreshing = () => {
  if (!_authProgress.REFRESHING) {
    return true
  }

  return new Promise((resolve, reject) => {
    const interval = setInterval(() => {
      if (!_authProgress.REFRESHING) {
        clearInterval(interval)
        resolve(true)
      }
    }, _authProgress.INTERVAL_MS)
  })
}

const handleErrorResponse = (error: AxiosError) => {
  // handler 401/clear session
  if (error.response && error.response.status === 401) {
    const refreshToken = getRefreshToken()
    if (!refreshToken) {
      clearCredentials()
      throw error
    }

    const config = error.config
    if (config) {
      return axios
        .post(refreshTokenUrl, { refreshJwt: refreshToken })
        .then(() => axios(injectAuthorization(config)))
        .catch(() => {
          clearCredentials()
          throw error
        })
    }
  }

  throw error
}

/**
 * @param {AxiosRequestConfig} config
 * @returns {AxiosResponse}
 */
const handleNormalRequest = (config: AxiosRequestConfig) => {
  injectAuthorization(config)

  return axios
    .create(Object.assign(config, { adapter: defaultAdapter }))
    .request(config)
    .then(response => cacheResponseToken(response))
    .catch(handleErrorResponse)
}

/**
 * @param {AxiosRequestConfig} config
 * @returns {AxiosResponse}
 */
const handleRefreshToken = (config: AxiosRequestConfig) => {
  _authProgress.REFRESHING = true
  return axios
    .create(Object.assign(config, { adapter: defaultAdapter }))
    .request(config)
    .then(response => {
      _authProgress.RESPONSE = response
      _authProgress.REFRESHING = false
      setTimeout(() => {
        _authProgress.RESPONSE = null
      }, _authProgress.CACHE_MS)

      cacheResponseToken(response)
      return response
    })
    .catch(error => {
      _authProgress.ERROR = error
      _authProgress.REFRESHING = false
      setTimeout(() => {
        _authProgress.ERROR = null
      }, _authProgress.CACHE_MS)

      clearCredentials()
      throw error
    })
}

/**
 * Custom axios adapter for cache
 */
const defaultAdapter = axiosHiringApi.defaults.adapter
axiosHiringApi.defaults.adapter = async function (config) {
  /**
   * SECTION: normal request
   */
  if (_authProgress.REFRESHING) {
    await awaitNotRefreshing()
  }

  const isCallRefresh = isRefreshRequest(config.url)
  if (!isCallRefresh) {
    return handleNormalRequest(config)
  }

  /**
   * SECTION: refresh token request
   */
  if (_authProgress.RESPONSE) {
    return _authProgress.RESPONSE
  }

  if (_authProgress.ERROR) {
    throw _authProgress.ERROR
  }

  return handleRefreshToken(config)
}

// IIFE load profile & listen storage update
ClientUtils.onlyClient(() => {
  /**
   * SECTION: initial user profile
   */
  const [token, refreshToken] = [
    AuthUtils.getToken(),
    AuthUtils.getToken(true)
  ]

  if (token) {
    ProfileApi.getProfile()
      .then(({ data }) => AuthModule.authenticated(data))
      .catch(() => clearCredentials())
  } else if (refreshToken) {
    axios
      .post(refreshTokenUrl, { refreshJwt: refreshToken })
      .then(({ data }) => AuthModule.authenticated(data.profile as IUserModel))
      .catch(() => clearCredentials())
  } else {
    // cookie use case
    ProfileApi.getProfile()
      .then(({ data }) => AuthModule.authenticated(data))
      .catch(() => clearCredentials())
  }

  /**
   * SECTION: sync storage from another tabs
   */
  window.addEventListener('storage', (event) => {
    if (event.oldValue === event.newValue || (event.key && ![AuthUtils.tokenKey, AuthUtils.refreshTokenKey].includes(event.key))) {
      return
    }

    // When token is removed, reset isAuthenticated in redux
    const token = LocalStorage.getItem(AuthUtils.tokenKey)
    const refreshToken = LocalStorage.getItem(AuthUtils.refreshTokenKey)
    if (!token && AuthUtils.getToken()) {
      clearCredentials()
    }

    if (token) {
      AuthUtils.setToken(token)
    }

    if (refreshToken) {
      AuthUtils.setToken(undefined, refreshToken)
    }
  })
})
